<template>
  <div class="page">
    <div class="page-bg" aria-hidden="true" />

    <p v-if="loading" class="state">加载中...</p>
    <div v-else-if="unavailable" class="state-block">
      <div class="state-icon" aria-hidden="true">
        <svg viewBox="0 0 48 48" width="48" height="48">
          <rect x="8" y="6" width="32" height="36" rx="4" fill="#E8ECF1"/>
          <path d="M16 18h16M16 24h12M16 30h14" stroke="#9AA3AF" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </div>
      <p class="state">暂不可用</p>
    </div>

    <template v-else>
      <header class="header">
        <p class="eyebrow">文件合集</p>
        <h1 class="title">{{ pageTitle }}</h1>
        <p class="meta">共 {{ items.length }} 个文件</p>
      </header>

      <ul class="list">
        <li
          v-for="item in items"
          :key="item.id"
          class="item"
          :class="[`type-${item.type}`, { disabled: !item.available }]"
          @click="onOpen(item)"
        >
          <span class="icon-wrap" aria-hidden="true">
            <!-- PDF -->
            <svg v-if="item.type === 'pdf'" class="icon" viewBox="0 0 40 40">
              <rect width="40" height="40" rx="10" fill="#FDECEC"/>
              <path d="M12 8h12l8 8v16a2 2 0 0 1-2 2H12a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2z" fill="#E53935"/>
              <path d="M24 8v6a2 2 0 0 0 2 2h6" fill="#FF8A80"/>
              <text x="20" y="28" text-anchor="middle" fill="#fff" font-size="8" font-weight="700" font-family="system-ui,sans-serif">PDF</text>
            </svg>
            <!-- Word -->
            <svg v-else-if="item.type === 'word'" class="icon" viewBox="0 0 40 40">
              <rect width="40" height="40" rx="10" fill="#E8F1FB"/>
              <path d="M12 8h12l8 8v16a2 2 0 0 1-2 2H12a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2z" fill="#2B579A"/>
              <path d="M24 8v6a2 2 0 0 0 2 2h6" fill="#5B9BD5"/>
              <text x="20" y="28" text-anchor="middle" fill="#fff" font-size="7" font-weight="700" font-family="system-ui,sans-serif">W</text>
            </svg>
            <!-- PPT -->
            <svg v-else-if="item.type === 'ppt'" class="icon" viewBox="0 0 40 40">
              <rect width="40" height="40" rx="10" fill="#FCEEE8"/>
              <path d="M12 8h12l8 8v16a2 2 0 0 1-2 2H12a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2z" fill="#C43E1C"/>
              <path d="M24 8v6a2 2 0 0 0 2 2h6" fill="#ED7D31"/>
              <text x="20" y="28" text-anchor="middle" fill="#fff" font-size="7" font-weight="700" font-family="system-ui,sans-serif">P</text>
            </svg>
            <!-- Excel -->
            <svg v-else-if="item.type === 'excel'" class="icon" viewBox="0 0 40 40">
              <rect width="40" height="40" rx="10" fill="#E7F4EC"/>
              <path d="M12 8h12l8 8v16a2 2 0 0 1-2 2H12a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2z" fill="#217346"/>
              <path d="M24 8v6a2 2 0 0 0 2 2h6" fill="#70AD47"/>
              <text x="20" y="28" text-anchor="middle" fill="#fff" font-size="7" font-weight="700" font-family="system-ui,sans-serif">X</text>
            </svg>
            <!-- Default file -->
            <svg v-else class="icon" viewBox="0 0 40 40">
              <rect width="40" height="40" rx="10" fill="#EEF1F5"/>
              <path d="M12 8h12l8 8v16a2 2 0 0 1-2 2H12a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2z" fill="#6B7280"/>
              <path d="M24 8v6a2 2 0 0 0 2 2h6" fill="#9CA3AF"/>
              <path d="M15 22h10M15 26h7" stroke="#fff" stroke-width="1.6" stroke-linecap="round"/>
            </svg>
          </span>

          <div class="body">
            <p class="name">{{ item.name }}</p>
            <p class="sub">
              <span v-if="!item.available">暂不可用</span>
              <span v-else>{{ typeLabel(item.type) }}</span>
            </p>
          </div>

          <span v-if="item.available" class="chevron" aria-hidden="true">
            <svg viewBox="0 0 20 20" width="18" height="18">
              <path d="M7.5 4.5L13 10l-5.5 5.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </li>
      </ul>
    </template>
  </div>
</template>

<script>
import {
  getCollect,
  getFileDisplayName,
  getFileType,
  isFileAvailable,
  resolveFileUrl,
} from '@/utils/files.data'

const TYPE_LABELS = {
  pdf: 'PDF 文档',
  word: 'Word 文档',
  ppt: 'PPT 演示文稿',
  excel: 'Excel 表格',
  file: '文件',
}

export default {
  name: 'MaterialList',
  data () {
    return {
      loading: true,
      unavailable: false,
      pageTitle: '',
      items: [],
    }
  },
  async created () {
    const { collect: collectId } = this.$route.params
    const collect = await getCollect(collectId)

    if (!collect || !collect.listable) {
      this.unavailable = true
      this.loading = false
      document.title = '暂不可用'
      return
    }

    this.pageTitle = collect.title || collect.collect_introtext || '文件列表'
    document.title = this.pageTitle

    const filePath = collect.file_path || ''
    this.items = (collect.children || []).map((file, index) => {
      const available = isFileAvailable(file, filePath)
      return {
        id: file.id,
        name: getFileDisplayName(file, index),
        type: getFileType(file),
        available,
        url: available ? resolveFileUrl(file, filePath) : '',
      }
    })
    this.loading = false
  },
  methods: {
    typeLabel (type) {
      return TYPE_LABELS[type] || TYPE_LABELS.file
    },
    onOpen (item) {
      if (!item.available || !item.url) return
      window.location.href = item.url
    },
  },
}
</script>

<style scoped>
.page {
  position: relative;
  min-height: 100vh;
  box-sizing: border-box;
  padding: 28px 16px 48px;
  color: #1c2430;
  font-family: 'PingFang SC', 'Hiragino Sans GB', 'Noto Sans SC', -apple-system, BlinkMacSystemFont, sans-serif;
}

.page-bg {
  position: fixed;
  inset: 0;
  z-index: -1;
  background:
    radial-gradient(ellipse 80% 50% at 10% -10%, rgba(47, 128, 237, 0.12), transparent 55%),
    radial-gradient(ellipse 70% 45% at 100% 0%, rgba(39, 174, 96, 0.08), transparent 50%),
    linear-gradient(180deg, #f3f6fa 0%, #e9eef5 100%);
}

.header {
  margin: 0 4px 20px;
}

.eyebrow {
  margin: 0 0 6px;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #7a8699;
}

.title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.35;
  letter-spacing: -0.02em;
}

.meta {
  margin: 8px 0 0;
  font-size: 13px;
  color: #7a8699;
}

.list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 14px 14px 12px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(28, 36, 48, 0.04), 0 8px 24px rgba(28, 36, 48, 0.05);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
  -webkit-tap-highlight-color: transparent;
}

.item:active:not(.disabled) {
  transform: scale(0.985);
  box-shadow: 0 1px 2px rgba(28, 36, 48, 0.04);
  background: #fff;
}

.item.disabled {
  opacity: 0.62;
  cursor: default;
  box-shadow: none;
}

.icon-wrap {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
}

.icon {
  display: block;
  width: 40px;
  height: 40px;
}

.body {
  flex: 1;
  min-width: 0;
}

.name {
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.4;
  word-break: break-word;
}

.sub {
  margin: 4px 0 0;
  font-size: 12px;
  color: #8b95a5;
  line-height: 1.3;
}

.item.disabled .sub {
  color: #b0b7c3;
}

.chevron {
  flex-shrink: 0;
  color: #c0c7d2;
  display: flex;
  align-items: center;
}

.state,
.state-block {
  margin: 0;
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #8b95a5;
  font-size: 15px;
}

.state-icon {
  margin-bottom: 12px;
  opacity: 0.9;
}

@media (min-width: 640px) {
  .page {
    max-width: 560px;
    margin: 0 auto;
    padding-top: 40px;
  }
}
</style>
